import {
  isAgenticStreamEventName,
  type AgenticStreamEvent,
} from "~shared/agenticChat/agenticStreamEvents";
import type { VercelUiStreamPart } from "./types";

/**
 * Legacy-shaped stream chunk delivered to persistentChatStore.
 * `type` is an optional client-only adapter hint (e.g. prose_chunk data parts).
 */
export type AgenticLegacyStreamChunk = AgenticStreamEvent & {
  type?: string;
};

export interface AgenticVercelStreamConsumerHandlers {
  onChunk: (chunk: AgenticLegacyStreamChunk) => void;
}

type PendingTool = {
  toolName: string;
  input?: unknown;
};

const TOOL_OUTPUT_STRUCTURE_KEYS = new Set(["type", "toolCallId", "toolName", "output"]);
const TOOL_INPUT_START_STRUCTURE_KEYS = new Set(["type", "toolCallId", "toolName"]);

/**
 * Converts Vercel UI message stream parts into the legacy-shaped chunks that
 * persistentChatStore handlers expect. The web app consumes Vercel wire format
 * exclusively (SW-9957); this module is the single adapter at the UI boundary.
 */
export class AgenticVercelStreamConsumer {
  private readonly pendingTools = new Map<string, PendingTool>();

  constructor(private readonly handlers: AgenticVercelStreamConsumerHandlers) {}

  consume(part: VercelUiStreamPart, options?: { skipText?: boolean }): void {
    const type = typeof part.type === "string" ? part.type : "";
    const skipText = options?.skipText === true;

    switch (type) {
      case "start":
      case "text-start":
      case "reasoning-start":
      case "reasoning-end":
        return;

      case "text-delta": {
        if (skipText) return;
        const delta = typeof part.delta === "string" ? part.delta : "";
        if (!delta) return;
        this.handlers.onChunk({ event: "text_chunk", text: delta });
        return;
      }

      case "text-end":
        // Structural close of a Vercel text block (also emitted before tools/reasoning/
        // complete). Must NOT wipe accumulated streaming text — intentional clears arrive
        // as `data-text_clear` from legacyToVercelParts.
        return;

      case "reasoning-delta": {
        const delta = typeof part.delta === "string" ? part.delta : "";
        if (!delta) return;
        this.handlers.onChunk({ event: "reasoning_chunk", text: delta });
        return;
      }

      case "tool-input-start": {
        const toolCallId = typeof part.toolCallId === "string" ? part.toolCallId : "";
        const toolName = typeof part.toolName === "string" ? part.toolName : "tool";
        const metadata: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(part)) {
          if (!TOOL_INPUT_START_STRUCTURE_KEYS.has(key) && value !== undefined) {
            metadata[key] = value;
          }
        }
        if (toolCallId) {
          this.pendingTools.set(toolCallId, { toolName });
        }
        this.handlers.onChunk({
          event: "tool_start",
          callId: toolCallId,
          tool: toolName,
          ...metadata,
        });
        return;
      }

      case "tool-input-available": {
        const toolCallId = typeof part.toolCallId === "string" ? part.toolCallId : "";
        const toolName = typeof part.toolName === "string" ? part.toolName : "tool";
        const input = part.input;
        if (toolCallId) {
          this.pendingTools.set(toolCallId, { toolName, input });
        }
        return;
      }

      case "tool-output-available": {
        const toolCallId = typeof part.toolCallId === "string" ? part.toolCallId : "";
        const pending = toolCallId ? this.pendingTools.get(toolCallId) : undefined;
        const toolName =
          pending?.toolName
          ?? (typeof part.toolName === "string" ? part.toolName : "tool");
        const metadata: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(part)) {
          if (!TOOL_OUTPUT_STRUCTURE_KEYS.has(key) && value !== undefined) {
            metadata[key] = value;
          }
        }
        const output = part.output;
        this.handlers.onChunk({
          event: "tool_complete",
          callId: toolCallId,
          tool: toolName,
          arguments: pending?.input,
          input: pending?.input,
          ...(output !== undefined ? { rawResult: output, result: output } : {}),
          ...metadata,
        });
        if (toolCallId) {
          this.pendingTools.delete(toolCallId);
        }
        return;
      }

      case "finish":
        this.handlers.onChunk({ event: "complete" });
        return;

      case "error": {
        const message =
          typeof part.errorText === "string"
            ? part.errorText
            : "An error has occurred. Please try again later.";
        this.handlers.onChunk({ event: "error", message });
        return;
      }

      default:
        if (type.startsWith("data-")) {
          const event = type.slice("data-".length);
          if (!isAgenticStreamEventName(event)) {
            return;
          }
          const data =
            part.data && typeof part.data === "object" && !Array.isArray(part.data)
              ? (part.data as Record<string, unknown>)
              : {};
          const chunk = { event, ...data } as AgenticLegacyStreamChunk;
          if (event === "prose_chunk") {
            chunk.type = "prose_chunk";
          }
          this.handlers.onChunk(chunk);
        }
    }
  }
}
